/**
 * AccountInfoDialog — read-only modal with the full account record
 * (Roomgrid review-style: micro labels + values, sections split by
 * hairlines). Fed from the list row, so it opens instantly.
 */

import React from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslate } from "react-admin";
import { StatusChip, ChannelChip } from "@/components/common";
import type { AccountListRow } from "../../types/account";

const i18n = "resources.accounts";

const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
};

interface RowProps {
  label: string;
  children: React.ReactNode;
  mono?: boolean;
}

const InfoRow: React.FC<RowProps> = ({ label, children, mono }) => {
  const theme = useTheme();
  const rg = theme.custom.rg;
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "170px 1fr",
        gap: "10px",
        alignItems: "center",
        py: "7px",
        borderBottom: `1px solid ${rg.border}`,
        "&:last-of-type": { borderBottom: "none" },
      }}
    >
      <Typography
        component="span"
        sx={{
          fontSize: "10.5px",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          fontWeight: 600,
          color: rg.text3,
        }}
      >
        {label}
      </Typography>
      <Box
        component="span"
        className={mono ? "rg-mono" : undefined}
        sx={{
          fontSize: mono ? "12px" : "13px",
          display: "flex",
          alignItems: "center",
          gap: "5px",
          flexWrap: "wrap",
          minWidth: 0,
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

const InfoSection: React.FC<{ label: string }> = ({ label }) => {
  const theme = useTheme();
  const rg = theme.custom.rg;
  return (
    <Typography
      sx={{
        fontSize: "10.5px",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        fontWeight: 600,
        color: rg.accent,
        pt: "14px",
        pb: "4px",
      }}
    >
      {label}
    </Typography>
  );
};

export interface AccountInfoDialogProps {
  record: AccountListRow | null;
  open: boolean;
  onClose: () => void;
}

export const AccountInfoDialog: React.FC<AccountInfoDialogProps> = ({
  record,
  open,
  onClose,
}) => {
  const translate = useTranslate();
  const t = (key: string, fallback?: string) =>
    translate(`${i18n}.${key}`, { _: fallback ?? key, smart_count: 1 });

  if (!record) return null;

  const acc = record.accountResponse;
  const apps = record.applicationResponse ?? [];
  const providers = record.providerResponse ?? [];
  const groups = (record.groupResponse ?? [])
    .flatMap((g) => g.groups.map((sub) => sub.groupName))
    .filter(Boolean);
  const users = (record.userResponse ?? []).map((u) => u.name).filter(Boolean);
  const channels = acc?.communicationTypes ?? [];
  const ips = acc?.whiteListedIPs?.iPs ?? [];
  const none = t("info.none", "—");
  const yes = t("info.yes", "Yes");
  const no = t("info.no", "No");

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: "10px" }}>
          {acc?.accountName}
          <Typography
            component="span"
            sx={{
              fontSize: "11.5px",
              fontWeight: 400,
              color: "text.secondary",
            }}
          >
            {t("info.title", "Account info")}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: "6px !important" }}>
        <InfoSection label={t("info.general", "General")} />
        <InfoRow label={t("fields.accountStatus")}>
          <StatusChip
            value={acc?.accountStatus}
            translatePrefix={`${i18n}.status`}
          />
        </InfoRow>
        <InfoRow label={t("fields.log")}>
          {acc?.loggingStatus
            ? translate(`${i18n}.logging.${acc.loggingStatus}`, {
                _: acc.loggingStatus,
              })
            : none}
        </InfoRow>
        <InfoRow label={t("fields.costCenter")}>
          {acc?.costCenterName || none}
        </InfoRow>
        <InfoRow label={t("info.channels", "Channels")}>
          {channels.length
            ? channels.map((c) => <ChannelChip key={c} channel={c} />)
            : none}
        </InfoRow>

        <InfoSection label={t("info.license", "License")} />
        <InfoRow label={t("fields.license")}>
          {record.licenseResponse?.name || none}
        </InfoRow>
        <InfoRow label={t("fields.licenseValidity")} mono>
          {formatDate(record.validityTimeFrame) || none}
        </InfoRow>
        <InfoRow label={t("fields.licenseBlocked")}>
          {record.isLicenseBlocked ? (
            <StatusChip value="blocked" translatePrefix={`${i18n}.status`} />
          ) : (
            no
          )}
        </InfoRow>

        <InfoSection label={t("info.limits", "Rate limits")} />
        <InfoRow label={t("fields.rateLimitApplies")}>
          {acc?.rateLimitApplies ? yes : no}
        </InfoRow>
        {acc?.rateLimitApplies && (
          <>
            <InfoRow label={t("fields.rateLimitPerMin")} mono>
              {String(acc?.rateLimitPerMin ?? 0)}
            </InfoRow>
            <InfoRow label={t("fields.rateLimitPerDay")} mono>
              {String(acc?.rateLimitPerDay ?? 0)}
            </InfoRow>
          </>
        )}
        <InfoRow label={t("info.whitelist", "Whitelisted IPs")} mono>
          {ips.length ? ips.join(" · ") : no}
        </InfoRow>

        <InfoSection label={t("info.applications", "Applications")} />
        <InfoRow label={t("fields.applications", "Application(s)")}>
          {apps.length
            ? apps.map((a) => <Chip key={a.id} size="small" label={a.name} />)
            : none}
        </InfoRow>

        <InfoSection label={t("info.providers", "Providers")} />
        <InfoRow label={t("fields.providers", "Provider(s)")}>
          {providers.length
            ? providers.map((p) => (
                <Chip key={p.id} size="small" label={p.providerName} />
              ))
            : none}
        </InfoRow>

        <InfoSection label={t("info.groups", "Groups")} />
        <InfoRow label={t("fields.groups")}>
          {groups.length ? groups.join(", ") : none}
        </InfoRow>

        <InfoSection label={t("info.users", "Users")} />
        <InfoRow label={t("fields.users")}>
          {users.length ? users.join(", ") : none}
        </InfoRow>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          {t("actions.close", "Close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AccountInfoDialog;
